import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, Scan, ImageIcon, FileText, Globe, Video,
  ArrowRight, ChevronDown, Star, Building2, Zap,
  CheckCircle, Users, Brain, Clapperboard, Layers,
  GraduationCap, Newspaper, Briefcase, FlaskConical,
  BookOpen, ExternalLink, Sparkles
} from 'lucide-react';

const B = 'rgb(37,99,235)';
const Blight = 'rgba(37,99,235,0.12)';
const Bborder = 'rgba(37,99,235,0.25)';

// ─── Floating particle background ─────────────────────────────────────────────
function ParticleField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.2 + 0.3,
      o: Math.random() * 0.3 + 0.05,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(37,99,235,${p.o})`;
        ctx.fill();
      });
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(37,99,235,${0.07 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} />;
}

// ─── Animated counter ─────────────────────────────────────────────────────────
function Counter({ to, suffix = '', duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const start = Date.now();
        const tick = () => {
          const p = Math.min((Date.now() - start) / duration, 1);
          setVal(Math.floor(p * to));
          if (p < 1) requestAnimationFrame(tick);
        };
        tick();
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

// ─── Scan demo animation ───────────────────────────────────────────────────────
function ScanDemo() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % 3), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative w-full max-w-sm mx-auto">
      <div className="bg-zinc-900/80 backdrop-blur border border-zinc-700/60 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
          <span className="ml-2 font-mono text-xs text-zinc-500">veritas-scan.com</span>
        </div>
        <div className="p-5 space-y-4">
          {/* File preview */}
          <div className="bg-zinc-800/60 border border-zinc-700/40 rounded-xl p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: Blight, border: `1px solid ${Bborder}` }}>
              <ImageIcon className="w-5 h-5" style={{ color: B }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-zinc-200 font-mono text-xs">imagen_perfil.jpg</p>
              <p className="text-zinc-500 font-mono text-xs">2.4 MB · JPEG</p>
            </div>
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div key="a" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: B }} />
                  <span className="font-mono text-xs" style={{ color: B }}>Analizando</span>
                </motion.div>
              )}
              {step === 1 && (
                <motion.div key="b" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5">
                  <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: `${B} transparent transparent transparent` }} />
                  <span className="font-mono text-xs text-zinc-400">Procesando</span>
                </motion.div>
              )}
              {step === 2 && (
                <motion.div key="c" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="font-mono text-xs text-emerald-400">Listo</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Scan area — altura fija */}
          <div className="relative bg-zinc-800/40 rounded-lg overflow-hidden" style={{ height: 120 }}>
            <AnimatePresence mode="wait">
              {step < 2 ? (
                <motion.div key="scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="absolute inset-0">
                  <motion.div
                    animate={{ y: [0, 108, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    className="absolute left-0 right-0 h-0.5"
                    style={{
                      background: `linear-gradient(to right, transparent, ${B}, transparent)`,
                      boxShadow: `0 0 12px 2px rgba(37,99,235,0.6)`
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Scan className="w-8 h-8 text-zinc-700" />
                  </div>
                </motion.div>
              ) : (
                <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="absolute inset-0 flex flex-col justify-center px-4 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="font-mono text-xs text-zinc-300 uppercase tracking-wider">Generada por IA</span>
                    </div>
                    <span className="font-mono text-xs font-bold text-red-400">96%</span>
                  </div>
                  <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: '96%' }} transition={{ duration: 0.8, ease: 'easeOut' }}
                      className="h-full rounded-full bg-gradient-to-r from-red-500 to-red-400" />
                  </div>
                  <p className="text-zinc-500 font-mono text-xs leading-relaxed">
                    Artefactos en manos (+6 dedos), piel sin poros, bokeh sintético.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <div className="absolute -inset-4 rounded-3xl blur-2xl pointer-events-none"
        style={{ background: 'rgba(37,99,235,0.08)', zIndex: -1 }} />
    </div>
  );
}

// ─── Feature card ──────────────────────────────────────────────────────────────
function FeatureCard({ icon: Icon, color, title, desc, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }} viewport={{ once: true }}
      className={`group relative bg-zinc-900/60 border border-zinc-800 hover:border-zinc-600 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1`}>
      <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl border flex items-center justify-center mb-3 sm:mb-4 ${color}`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>
      <h3 className="text-white font-bold text-sm sm:text-base mb-1.5 sm:mb-2 tracking-tight">{title}</h3>
      <p className="text-zinc-500 text-xs sm:text-sm leading-relaxed hidden sm:block">{desc}</p>
    </motion.div>
  );
}

// ─── Use case card ─────────────────────────────────────────────────────────────
function UseCaseCard({ icon: Icon, title, desc, color, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45 }} viewport={{ once: true }}
      className={`relative overflow-hidden bg-zinc-900/50 border rounded-2xl p-5 transition-all hover:scale-[1.02] ${color}`}>
      <Icon className="w-6 h-6 sm:w-7 sm:h-7 mb-2 sm:mb-3 opacity-80" />
      <h3 className="text-white font-bold text-xs sm:text-sm mb-1 sm:mb-1.5">{title}</h3>
      <p className="text-zinc-400 text-xs leading-relaxed hidden sm:block">{desc}</p>
    </motion.div>
  );
}

// ─── Plan card ─────────────────────────────────────────────────────────────────
function PlanCard({ plan, price, period, features, cta, highlighted = false, delay = 0, onCta }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }} viewport={{ once: true }}
      className="relative flex flex-col rounded-2xl p-6 border transition-all bg-zinc-900/60 border-zinc-800"
      style={highlighted ? { borderColor: Bborder, boxShadow: `0 20px 40px rgba(37,99,235,0.08)` } : {}}>
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-white font-mono text-xs font-bold"
          style={{ background: B }}>
          Más popular
        </div>
      )}
      <div className="mb-6">
        <p className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">{plan}</p>
        <div className="flex items-end gap-1">
          <span className="text-4xl font-black text-white tracking-tight">{price}</span>
          {period && <span className="text-zinc-500 font-mono text-sm mb-1">{period}</span>}
        </div>
      </div>
      <ul className="space-y-2.5 flex-1 mb-6">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-zinc-300 text-sm">
            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: B }} />
            {f}
          </li>
        ))}
      </ul>
      <button onClick={onCta}
        className="w-full py-3 rounded-xl font-mono text-sm font-bold transition-all"
        style={highlighted
          ? { background: B, color: '#fff', boxShadow: `0 8px 24px rgba(37,99,235,0.3)` }
          : {}
        }
        {...(!highlighted ? { className: 'w-full py-3 rounded-xl font-mono text-sm font-bold transition-all bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700' } : {})}>
        {cta}
      </button>
    </motion.div>
  );
}

// ─── Main Landing ──────────────────────────────────────────────────────────────
export default function Landing({ onEnter, onNavigate, moduleConfig = {} }) {
  const imgEnabled = moduleConfig.image_enabled !== false;
  const vidEnabled = moduleConfig.video_enabled !== false;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.6], [0, -60]);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-zinc-950 text-white overflow-x-hidden">

      {/* ── NAV ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800/60 bg-zinc-950/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Scan className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: B }} />
            <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">VERITAS <span style={{ color: B }}>SCAN</span></h1>
          </div>
          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-6">
            {[['Funciones', 'features'], ['Casos de uso', 'usecases'], ['Precios', 'pricing'], ['Blog', null]].map(([label, id]) => (
              <button key={label} onClick={() => id ? scrollTo(id) : onNavigate('blog')}
                className="text-zinc-400 hover:text-white font-mono text-xs transition-colors">
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => onNavigate('auth')}
              className="hidden sm:block px-3 py-1.5 font-mono text-xs text-zinc-400 hover:text-white transition-colors">
              Iniciar sesión
            </button>
            <button onClick={onEnter}
              className="px-3 sm:px-4 py-1.5 text-white font-mono text-xs font-bold rounded-lg transition-all whitespace-nowrap"
              style={{ background: B, boxShadow: `0 4px 12px rgba(37,99,235,0.25)` }}>
              Empezar gratis
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center pt-14 pb-8 overflow-hidden">
        <ParticleField />

        {/* Radial glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[700px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(37,99,235,0.16) 0%, rgba(37,99,235,0.05) 45%, transparent 72%)' }} />

        <motion.div style={{ opacity: heroOpacity, y: heroY }}
          className="relative z-10 text-center max-w-4xl mx-auto px-4 sm:px-6">

          {/* Badge */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 mt-8 sm:mt-4"
            style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.3)' }}>
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: B }} />
            <span className="font-mono text-xs" style={{ color: 'rgb(147,175,255)' }}>Powered by Gemini 2.5 Flash · 94% precisión</span>
          </motion.div>

          {/* Headline */}
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] mb-5">
            ¿Real o<br />
            <span className="relative inline-block">
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: `linear-gradient(135deg, rgb(96,165,250) 0%, ${B} 50%, rgb(29,78,216) 100%)` }}>
                generado por IA?
              </span>
              <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -bottom-1 left-0 right-0 h-px"
                style={{ background: `linear-gradient(to right, transparent, ${B}, transparent)` }} />
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="text-zinc-400 text-base sm:text-xl leading-relaxed mb-7 max-w-2xl mx-auto px-2">
            Detecta imágenes deepfake, textos de ChatGPT, vídeos sintéticos y documentos de IA.
            En segundos, con explicación forense detallada.
          </motion.p>

          {/* CTAs */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8 px-4 w-full">
            <button onClick={onEnter}
              className="group flex items-center gap-2 px-6 py-3 sm:py-3.5 text-white font-bold rounded-xl transition-all text-sm w-full sm:w-auto justify-center"
              style={{ background: B, boxShadow: `0 20px 40px rgba(37,99,235,0.3)` }}>
              Analizar contenido ahora
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => scrollTo('features')}
              className="flex items-center gap-2 px-6 py-3 sm:py-3.5 bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 font-mono text-sm rounded-xl transition-all w-full sm:w-auto justify-center">
              Ver cómo funciona
              <ChevronDown className="w-4 h-4" />
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="flex items-center justify-center gap-6 sm:gap-12">
            {[{ n: 94, suf: '%', label: 'Precisión' }, { n: 5, suf: '+', label: 'Formatos' }, { n: 100, suf: '%', label: 'Privado' }].map(({ n, suf, label }) => (
              <div key={label} className="text-center">
                <p className="text-2xl font-black text-white"><Counter to={n} suffix={suf} /></p>
                <p className="text-zinc-600 font-mono text-xs uppercase tracking-wider mt-0.5">{label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Demo */}
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }}
          className="relative z-10 w-full max-w-sm mx-auto mt-10 px-4">
          <ScanDemo />
        </motion.div>

        {/* Scroll indicator */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5">
          <span className="font-mono text-xs text-zinc-600">Scroll</span>
          <motion.div animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
            <ChevronDown className="w-4 h-4 text-zinc-600" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="py-14 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-10">
            <p className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: B }}>Capacidades</p>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight">Todo tipo de contenido,<br className="hidden sm:block" /> un solo detector</h2>
            <p className="text-zinc-500 mt-4 max-w-xl mx-auto text-sm leading-relaxed">
              Veritas Scan analiza cualquier formato con modelos forenses especializados para cada tipo de contenido.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <FeatureCard delay={0}    icon={ImageIcon} color="text-blue-400 border-blue-500/20 bg-blue-500/10"    title="Imágenes & Deepfakes"      desc="Detecta imágenes generadas por Midjourney, DALL-E, Stable Diffusion o Flux. Análisis forense de artefactos, manos, piel y sombras." />
            <FeatureCard delay={0.07} icon={FileText}  color="text-emerald-400 border-emerald-500/20 bg-emerald-500/10" title="Textos & Documentos"   desc="Identifica textos de ChatGPT, Claude, Gemini o Llama. Analiza burstiness, marcadores lingüísticos y personalidad del autor." />
            <FeatureCard delay={0.14} icon={Video}     color="text-red-400 border-red-500/20 bg-red-500/10"       title="Vídeos sintéticos"         desc="Detecta vídeos generados por Sora, Runway o Kling, y deepfakes faciales. Análisis temporal fotograma a fotograma." />
            <FeatureCard delay={0.21} icon={Globe}     color="text-purple-400 border-purple-500/20 bg-purple-500/10" title="Páginas web & URLs"      desc="Analiza el contenido textual de cualquier URL. Detecta blogs SEO generados automáticamente y artículos de IA." />
            <FeatureCard delay={0.28} icon={Layers}    color="text-amber-400 border-amber-500/20 bg-amber-500/10"  title="Análisis por lotes"        desc="Sube hasta 50 archivos de golpe y obtén un informe consolidado. Perfecto para auditorías y revisiones masivas." />
            <FeatureCard delay={0.35} icon={Brain}     color="text-zinc-300 border-zinc-600 bg-zinc-800/60"        title="Informe forense detallado" desc="Cada análisis incluye veredicto, confianza, razonamiento forense paso a paso e indicadores concretos detectados." />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 border-y border-zinc-800/60"
        style={{ background: `linear-gradient(180deg, transparent 0%, rgba(37,99,235,0.03) 50%, transparent 100%)` }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-10">
            <p className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: B }}>Proceso</p>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight">Cuatro pasos,<br />resultado en segundos</h2>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { n: '01', title: 'Sube el contenido',    desc: 'Arrastra imagen, vídeo, PDF o pega texto / URL. Sin registro obligatorio.', icon: UploadIcon },
              { n: '02', title: 'Gemini lo analiza',    desc: 'Gemini 2.5 Flash examina cientos de patrones forenses en menos de 5 segundos.', icon: Brain },
              { n: '03', title: 'Obtienes el veredicto',desc: 'Veredicto claro, porcentaje de confianza y explicación forense detallada.', icon: ShieldCheck },
              { n: '04', title: 'Exporta el informe',   desc: 'Descarga el informe PDF (Pro) o guárdalo en tu historial en la nube.', icon: BookOpen },
            ].map(({ n, title, desc, icon: Icon }, i) => (
              <motion.div key={n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }} className="relative">
                <div className="text-zinc-800 font-black text-4xl sm:text-6xl leading-none mb-3 select-none">{n}</div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" style={{ color: B }} />
                  <h3 className="text-white font-bold text-xs sm:text-sm">{title}</h3>
                </div>
                <p className="text-zinc-500 text-xs leading-relaxed hidden sm:block">{desc}</p>
                {i < 3 && <div className="hidden lg:block absolute top-8 -right-3 text-zinc-700"><ArrowRight className="w-4 h-4" /></div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── USE CASES ── */}
      <section id="usecases" className="py-14 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-10">
            <p className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: B }}>¿Para quién?</p>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight">Usado por profesionales<br className="hidden sm:block" /> que necesitan la verdad</h2>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <UseCaseCard delay={0}    icon={Newspaper}     title="Periodistas"      desc="Verifica imágenes y artículos antes de publicar. Evita difundir desinformación generada por IA."  color="border-zinc-700 hover:border-zinc-500" />
            <UseCaseCard delay={0.08} icon={GraduationCap} title="Profesores"       desc="Detecta trabajos generados por ChatGPT. Mantén la integridad académica en tu aula."                color="border-zinc-700 hover:border-zinc-500" />
            <UseCaseCard delay={0.16} icon={Briefcase}     title="RRHH & Empresas"  desc="Identifica CVs y portfolios generados con IA sin declararlo. Selección más justa."                  color="border-zinc-700 hover:border-zinc-500" />
            <UseCaseCard delay={0.24} icon={FlaskConical}  title="Investigadores"   desc="Analiza la proliferación de contenido artificial en redes y medios con análisis masivo."            color="border-zinc-700 hover:border-zinc-500" />
          </div>
        </div>
      </section>

      {/* ── VERITAS SUITE ── */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 border-y border-zinc-800/60"
        style={{ background: `linear-gradient(180deg, transparent 0%, rgba(37,99,235,0.03) 50%, transparent 100%)` }}>
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-8">
            <p className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: B }}>Suite completa</p>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight">Más allá de la detección</h2>
            <p className="text-zinc-500 mt-3 max-w-xl mx-auto text-sm">Veritas también incluye herramientas de generación con IA de última generación.</p>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: 'VERITAS SCAN',    color: 'text-blue-400',   border: 'border-blue-500/30 hover:border-blue-500/50',   icon: Scan,        bg: 'bg-blue-500/10',   desc: 'Detector forense de contenido IA. Imágenes, textos, vídeos, documentos y URLs.',     action: onEnter, show: true },
              { label: 'VERITAS IMAGE',   color: 'text-purple-400', border: 'border-purple-500/30 hover:border-purple-500/50', icon: ImageIcon,   bg: 'bg-purple-500/10', desc: 'Genera imágenes fotorrealistas con Google Imagen 4. Text-to-image e image-to-image.', action: () => onNavigate('image'), show: imgEnabled },
              { label: 'VERITAS VIDEO',   color: 'text-red-400',    border: 'border-red-500/30 hover:border-red-500/50',      icon: Clapperboard, bg: 'bg-red-500/10',   desc: 'Genera vídeos cinematográficos con Seedance 2.0. Text-to-video e image-to-video.',   action: () => onNavigate('video'), show: vidEnabled },
              { label: 'VERITAS CONTENT', color: 'text-pink-400',   border: 'border-pink-500/30 hover:border-pink-500/50',    icon: Sparkles,    bg: 'bg-pink-500/10',   desc: 'Red social de creaciones IA. Comparte, descubre y sigue a otros creadores.',         action: () => onNavigate('content'), show: true },
            ].filter(c => c.show).map(({ label, color, border, icon: Icon, bg, desc, action }, i) => (
              <motion.button key={label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }} onClick={action}
                className={`group relative text-left bg-zinc-900/50 border ${border} rounded-2xl p-6 transition-all hover:-translate-y-1`}>
                <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center mb-4 border ${border}`}>
                  <Icon className={`w-6 h-6 ${color}`} />
                </div>
                <p className={`font-black text-sm tracking-wide ${color} mb-1`}>{label}</p>
                <p className="text-zinc-400 text-xs leading-relaxed">{desc}</p>
                <div className="flex items-center gap-1 mt-4">
                  <span className={`font-mono text-xs ${color}`}>Abrir</span>
                  <ArrowRight className={`w-3 h-3 ${color} group-hover:translate-x-1 transition-transform`} />
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-14 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-10">
            <p className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: B }}>Precios</p>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight">Comienza gratis,<br />escala cuando lo necesites</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-start">
            <PlanCard delay={0}   plan="Free"     price="€0"  period=""     highlighted={false} features={['5 análisis/día', 'Imágenes, textos, PDFs', 'URLs y páginas web', 'Veredicto + confianza']}                                              cta="Empezar gratis"    onCta={onEnter} />
            <PlanCard delay={0.1} plan="Pro"      price="€9"  period="/mes" highlighted={true}  features={['Análisis ilimitados', 'Vídeos hasta 500MB', 'Historial en la nube', 'Exportar PDF', 'Análisis por lotes (10)', 'API 500 llamadas/día']} cta="Probar Pro"        onCta={() => onNavigate('precios')} />
            <PlanCard delay={0.2} plan="Business" price="€29" period="/mes" highlighted={false} features={['Todo lo de Pro', 'Lotes de 50 archivos', 'API 5.000 llamadas/día', '5 usuarios del equipo', 'Soporte prioritario']}                     cta="Hablar con ventas" onCta={() => onNavigate('precios')} />
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-14 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="relative bg-zinc-900/60 rounded-2xl sm:rounded-3xl p-7 sm:p-14 overflow-hidden border"
            style={{ borderColor: Bborder }}>
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: `radial-gradient(ellipse at 50% 100%, rgba(37,99,235,0.1) 0%, transparent 70%)` }} />
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6"
                style={{ background: Blight, border: `1px solid ${Bborder}` }}>
                <Scan className="w-7 h-7" style={{ color: B }} />
              </div>
              <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-4">
                La verdad importa.<br />Verifica antes de creer.
              </h2>
              <p className="text-zinc-400 text-base leading-relaxed mb-8 max-w-lg mx-auto">
                En un mundo inundado de contenido artificial, Veritas Scan es tu segunda opinión forense.
                Gratis, en segundos, sin instalaciones.
              </p>
              <button onClick={onEnter}
                className="group inline-flex items-center gap-2.5 px-6 sm:px-8 py-3.5 sm:py-4 text-white font-bold rounded-xl transition-all text-sm w-full sm:w-auto justify-center"
                style={{ background: B, boxShadow: `0 20px 40px rgba(37,99,235,0.3)` }}>
                Analizar mi primer contenido
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-zinc-600 font-mono text-xs mt-4">Sin tarjeta. Sin registro obligatorio.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-zinc-800/60 py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2">
            <Scan className="w-5 h-5" style={{ color: B }} />
            <span className="font-heading text-sm font-black tracking-tight text-zinc-400">VERITAS <span style={{ color: B }}>SCAN</span></span>
          </div>
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
            {[['Blog', () => onNavigate('blog')], ['Precios', () => onNavigate('precios')], ['API', () => onNavigate('api-docs')], ['Privacidad', () => onNavigate('privacidad')]].map(([l, fn]) => (
              <button key={l} onClick={fn} className="font-mono text-xs text-zinc-600 hover:text-zinc-400 transition-colors">{l}</button>
            ))}
          </div>
          <p className="font-mono text-xs text-zinc-700">© 2026 Veritas Scan</p>
        </div>
      </footer>
    </div>
  );
}

function UploadIcon({ className, style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
